'use client';

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole =
  | 'SUPER_ADMIN' | 'TENANT_OWNER' | 'TENANT_ADMIN' | 'MANAGER' | 'PRO_OFFICER' | 'ACCOUNTANT' | 'DOCUMENT_CONTROLLER' | 'CLIENT_USER';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string | null;
  phone?: string | null;
  role: UserRole;
  is_active: boolean;
}

export interface Tenant {
  id: string;
  company_name: string;
  trade_name?: string | null;
  email: string;
  phone?: string | null;
  emirate?: string | null;
  address?: string | null;
  logo_url?: string | null;
  default_currency?: string | null;
  subscription_plan: string;
  status: string;
  owner_user_id?: string | null;
}

interface AuthContextValue {
  user: any | null;
  session: any | null;
  profile: UserProfile | null;
  activeTenant: Tenant | null;
  userRole: UserRole | null;
  loading: boolean;
  error: string | null;
  profileError: string | null;
  tenantUserCount: number;
  signUp: (
    email: string,
    password: string,
    metadata?: Record<string, any>,
    options?: { emailRedirectTo?: string }
  ) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  refreshTenant: () => Promise<void>;
  isSuperAdmin: () => boolean;
  isTenantOwner: () => boolean;
  isTenantAdmin: () => boolean;
  hasRole: (roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [activeTenant, setActiveTenant] = useState<Tenant | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [tenantUserCount, setTenantUserCount] = useState(0);

  // Tracks which user ID has already had its profile loaded to prevent duplicate fetches
  const loadedForUserIdRef = useRef<string | null>(null);
  // Tracks if a profile load is currently in-flight
  const profileLoadingRef = useRef(false);

  // Stable ref to the singleton Supabase client — avoids re-creating on every render
  const supabaseRef = useRef<ReturnType<typeof createClient> | null>(null);
  const clientErrorRef = useRef<string | null>(null);
  if (!supabaseRef.current && !clientErrorRef.current) {
    try {
      supabaseRef.current = createClient();
    } catch (e: any) {
      clientErrorRef.current = e.message || 'Supabase not configured';
    }
  }
  const supabase = supabaseRef.current;
  const clientError = clientErrorRef.current;

  // Effect 1: Initialize session on mount and subscribe to auth state changes.
  // onAuthStateChange only sets session/user — never loads profile.
  useEffect(() => {
    if (clientError || !supabase) {
      setError('Supabase is not configured. Please set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY.');
      setLoading(false);
      return;
    }

    // getSession() to bootstrap initial state — does NOT call Auth API (reads from local storage)
    supabase.auth.getSession().then((res: any) => {
      const session = res?.data?.session ?? null;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(Boolean(session?.user));
    }).catch(() => {
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event: any, session: any) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (!session) {
        setProfile(null);
        setActiveTenant(null);
        setUserRole(null);
        setTenantUserCount(0);
        setProfileError(null);
        // Reset guards so next sign-in loads fresh profile
        loadedForUserIdRef.current = null;
        profileLoadingRef.current = false;
        setLoading(false);
        return;
      }

      const nextUserId = String(session?.user?.id || '');
      if (nextUserId && loadedForUserIdRef.current === nextUserId) {
        setLoading(false);
      } else {
        setLoading(Boolean(session?.user));
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2: Load profile and tenant whenever user?.id changes.
  // Reads directly from public.profiles — no RPC, no auto-create.
  // Guard prevents duplicate loads when getSession + onAuthStateChange both fire.
  // Only runs when session has a valid access_token (ensures authenticated requests).
  useEffect(() => {
    if (!supabase || !user?.id) {
      if (!user?.id) {
        setProfile(null);
        setActiveTenant(null);
        setUserRole(null);
        setTenantUserCount(0);
        setProfileError(null);
        loadedForUserIdRef.current = null;
        profileLoadingRef.current = false;
      }
      return;
    }

    const userId = user.id;

    // Skip if already loaded for this user or a load is in-flight
    if (loadedForUserIdRef.current === userId || profileLoadingRef.current) {
      return;
    }

    profileLoadingRef.current = true;

    const loadProfile = async () => {
      setProfileError(null);
      setLoading(true);

      try {
        // Confirm session and access_token before querying profiles
        const { data: { session: currentSession } } = await supabase.auth.getSession();

        if (!currentSession || !currentSession.access_token) {
          setProfileError('No authenticated session token available');
          profileLoadingRef.current = false;
          setLoading(false);
          return;
        }

        // Load profile directly from public.profiles — no Auth API call
        const { data: profileData, error: profileErr } = await supabase
          .from('profiles')
          .select('id, email, full_name, avatar_url, phone, role, is_active')
          .eq('id', userId)
          .maybeSingle();

        if (profileErr) {
          setProfileError(`Profile query failed: ${profileErr.message}`);
          return;
        }

        if (!profileData) {
          setProfileError(`No profile found for user id: ${userId}. Please contact your administrator.`);
          return;
        }

        setProfile(profileData as UserProfile);
        // Mark as loaded so subsequent triggers for same user are skipped
        loadedForUserIdRef.current = userId;

        // SUPER_ADMIN: skip tenant lookup entirely
        if (profileData.role === 'SUPER_ADMIN') {
          setUserRole('SUPER_ADMIN');
          setActiveTenant(null);
          setTenantUserCount(0);
          return;
        }

        // Load tenant_users for non-SUPER_ADMIN users
        const { data: tenantUserData, error: tuError } = await supabase
          .from('tenant_users')
          .select('tenant_id, role, tenants(*)')
          .eq('user_id', userId)
          .eq('is_active', true)
          .limit(1)
          .maybeSingle();

        const { count: tuCount } = await supabase
          .from('tenant_users')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('is_active', true);

        setTenantUserCount(tuCount || 0);

        if (tenantUserData && tenantUserData.tenants) {
          const tenantData = Array.isArray(tenantUserData.tenants)
            ? tenantUserData.tenants[0]
            : tenantUserData.tenants;
          setActiveTenant(tenantData as Tenant);
          setUserRole(tenantUserData.role as UserRole);
        } else {
          setActiveTenant(null);
          setUserRole((profileData.role as UserRole) || null);
        }
      } finally {
        profileLoadingRef.current = false;
        setLoading(false);
      }
    };

    loadProfile();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshTenant = async () => {
    if (!supabase || !user?.id) return;
    const userId = user.id;

    const { data: tenantUserData, error: tuError } = await supabase
      .from('tenant_users')
      .select('tenant_id, role, tenants(*)')
      .eq('user_id', userId)
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();

    if (tuError) {
      return;
    }

    const { count: tuCount } = await supabase
      .from('tenant_users')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_active', true);

    setTenantUserCount(tuCount || 0);

    if (tenantUserData && tenantUserData.tenants) {
      const tenantData = Array.isArray(tenantUserData.tenants)
        ? tenantUserData.tenants[0]
        : tenantUserData.tenants;
      setActiveTenant(tenantData as Tenant);
      setUserRole(tenantUserData.role as UserRole);
    } else {
      setActiveTenant(null);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    metadata: Record<string, any> = {},
    options: { emailRedirectTo?: string } = {}
  ) => {
    if (!supabase) throw new Error('Supabase not configured');
    const defaultRedirectTo = `${process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '')}/auth/callback`;
    const emailRedirectTo = options.emailRedirectTo || defaultRedirectTo;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || metadata?.full_name || '',
        },
        emailRedirectTo,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    return data;
  };

  const signOut = async () => {
    if (!supabase) throw new Error('Supabase not configured');
    const isExpectedMissingSession = (err: any) => {
      const message = String(err?.message || err?.error_description || err?.error || '').toLowerCase();
      const status = Number(err?.status || err?.statusCode || 0);
      return message.includes('auth session missing') || status === 403;
    };

    try {
      const { data: { session: existingSession } } = await supabase.auth.getSession();
      const hadSessionToken = Boolean(existingSession?.access_token);

      await supabase.auth.signOut({ scope: 'local' } as any);

      if (hadSessionToken) {
        const { error } = await supabase.auth.signOut();
        if (error && !isExpectedMissingSession(error)) throw error;
      }
    } catch (e: any) {
      if (!isExpectedMissingSession(e)) throw e;
    } finally {
      setSession(null);
      setUser(null);
      setProfile(null);
      setActiveTenant(null);
      setUserRole(null);
      setTenantUserCount(0);
      setProfileError(null);
      setError(null);
      loadedForUserIdRef.current = null;
      profileLoadingRef.current = false;
    }
  };

  const isSuperAdmin = () => profile?.role === 'SUPER_ADMIN' || userRole === 'SUPER_ADMIN';
  const isTenantOwner = () => userRole === 'TENANT_OWNER';
  const isTenantAdmin = () => userRole === 'TENANT_ADMIN' || userRole === 'TENANT_OWNER';
  const hasRole = (roles: UserRole[]) => {
    const effectiveProfileRole =
      profile?.role === 'SUPER_ADMIN' || profile?.role === 'CLIENT_USER'
        ? profile.role
        : null;
    const currentRole = userRole || effectiveProfileRole;
    return currentRole ? roles.includes(currentRole) : false;
  };

  const value: AuthContextValue = {
    user,
    session,
    profile,
    activeTenant,
    userRole,
    loading,
    error,
    profileError,
    tenantUserCount,
    signUp,
    signIn,
    signOut,
    refreshTenant,
    isSuperAdmin,
    isTenantOwner,
    isTenantAdmin,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
